import { useRef, useState, type RefObject } from 'react';
import { VariableMenu } from './variable-menu.component';

interface VariableInsertButtonProps {
  /**
   * Ref to the `<input>` or `<textarea>` the variable should be inserted
   * into. The button reads the current cursor position from this element
   * and splices `{{token}}` in place; the caller's `onChange` handler is
   * called with the new value.
   */
  targetRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  /** Current value of the input. */
  value: string;
  /** Called with the new value after a variable is inserted. */
  onChange: (next: string) => void;
  /** Optional aria-label for the trigger button. */
  ariaLabel?: string;
}

/**
 * `{{ }}` button rendered next to a plain-text input (subject, preheader,
 * button label, banner text, image alt/caption). Opens the shared variable
 * menu and inserts the chosen token at the input's caret position; if the
 * input is not focused, appends to the end.
 *
 * Plain-text fields keep variables as the literal `{{token}}` string —
 * we do not render chips inside an `<input>` because contentEditable would
 * be heavy for this v1. The render endpoint substitutes the tokens just
 * the same.
 */
export function VariableInsertButton({
  targetRef,
  value,
  onChange,
  ariaLabel = 'Variable einfügen',
}: VariableInsertButtonProps) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const handleInsert = (token: string) => {
    setOpen(false);
    const target = targetRef.current;
    const insertion = `{{${token}}}`;

    // Splice at the current selection range when the target is focused —
    // otherwise append. `selectionStart` is `null` for unfocused inputs.
    if (target && document.activeElement === target) {
      const start = target.selectionStart ?? value.length;
      const end = target.selectionEnd ?? value.length;
      const next = value.slice(0, start) + insertion + value.slice(end);
      onChange(next);
      // Restore the caret right after the inserted token on the next tick
      // (after React has re-rendered the input's value).
      const caret = start + insertion.length;
      requestAnimationFrame(() => {
        target.focus();
        target.setSelectionRange(caret, caret);
      });
      return;
    }

    onChange(`${value}${insertion}`);
  };

  return (
    <span className="eb-var-insert-wrap">
      <button
        ref={buttonRef}
        type="button"
        className={`eb-var-insert-btn${open ? ' is-open' : ''}`}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((o) => !o)}
      >
        {'{{ }}'}
      </button>
      {open && (
        <VariableMenu
          onSelect={handleInsert}
          onClose={() => setOpen(false)}
          anchorRef={buttonRef}
        />
      )}
    </span>
  );
}
